<script setup>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import MarketingCtaBtn from '~/components/marketing/MarketingCtaBtn.vue';
import SetupAssistantDemoStage from '~/components/marketing/SetupAssistantDemoStage.vue';
import TappyDemoStage from '~/components/marketing/TappyDemoStage.vue';

// The setup assistant and Tappy, our AI helper. The page runs the real
// components on a short scripted run, because the feature is the conversation.
definePageMeta({
  name: 'FeaturesSetupAssistant',
  path: '/features/setup-assistant',
});

const {setMeta} = useHeadService();
const {accountCtaLabel} = useMarketingCta();

setMeta(
    'Setup assistant and Tappy, our AI helper | Tap Leagues',
    'Describe your club in plain words and the setup assistant drafts the playing areas, competitions, memberships and booking rules for you to check and apply. Tappy, our AI helper, answers how-to questions for players and admins. Nothing changes without you.',
);

const DRAFTS = [
  {icon: 'mdi-map-marker-outline', name: 'Playing areas', blurb: 'Courts, tables and boards, with their sports.'},
  {icon: 'mdi-medal-outline', name: 'Competitions', blurb: 'Box leagues, ladders, tournaments, americano leagues and meets, with the players you name put in.'},
  {icon: 'mdi-counter', name: 'Scoring systems', blurb: 'The club\'s own, from a template, with a preview of how a score is entered.'},
  {icon: 'mdi-card-account-details-outline', name: 'Memberships and passes', blurb: 'From a description or a pasted price list. Created unpublished until you publish them.'},
  {icon: 'mdi-timetable', name: 'Booking rules', blurb: 'Hours, slot length, who can book and prices. Off for players until you switch bookings on.'},
];

const FAQS = [
  {q: 'Does it change my club by itself?', a: 'No. It drafts. You open the check page, see every item with what is live now and what it will be after, fix or leave out anything, and press Create setup or Apply now yourself. If any part fails, nothing changes.'},
  {q: 'Can I undo it?', a: 'Yes, for 7 days, while nobody has joined, paid or booked. After that it stays. An undo removes what the setup created and puts changed settings back; anything someone has joined, applied for, booked or edited since is kept and listed.'},
  {q: 'What can it not do?', a: 'It cannot add divisions, make a tournament draw, start a league\'s next cycle, change a running competition\'s format, archive or delete a competition, or change an existing scoring system. Those are done on their own pages, and the draft says so under Not possible yet.'},
  {q: 'Does it push me to set up other things?', a: 'No. A conversation about a competition goes straight to the competition. The whole-club conversation asks once what you use Tap Leagues for, and sets up only what you ticked.'},
  {q: 'What happens with names I type?', a: 'They are used to find players in your club, or to add them as new players when you apply. The assistant never sees your players\' contact or health details. Messages are stored without email addresses or phone numbers, so do not paste them.'},
  {q: 'What does Tappy do?', a: 'Tappy, our AI helper, answers questions about using Tap Leagues, in the language you write in, for players and club admins. It explains the steps; it never changes your club. When an admin\'s question is about setting the club up, it offers to open the setup assistant.'},
  {q: 'Can Tappy be wrong?', a: 'Yes, and the chat says so. Tappy only answers about using Tap Leagues; if it does not know, it says so and points you to your club admin or Tap Leagues support. It cannot see your account, results or messages.'},
  {q: 'What does it cost?', a: 'Nothing. Both are part of Tap Leagues, on the website and in the app. There are daily limits; when you reach one, you carry on in the forms, already filled in.'},
];

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {'@type': 'Answer', text: faq.a},
      })),
    }),
  }],
});
</script>

<template>
  <base-page>
    <!-- ===== HERO ===== -->
    <section class="fsa-hero">
      <div class="fsa-hero-glow" aria-hidden="true"/>
      <v-container class="fsa-wrap fsa-hero-inner">
        <NuxtLink to="/features" class="fsa-back"><v-icon size="16" icon="mdi-arrow-left"/> All features</NuxtLink>
        <span class="fsa-eyebrow">Setup assistant and Tappy</span>
        <h1 class="fsa-title">Describe the club. Check the draft. Apply it.</h1>
        <p class="fsa-lead">
          Tell the setup assistant what you want in plain words, or paste a price list. It asks only what changes the
          outcome, drafts the playing areas, competitions, memberships and booking rules, and shows you every item
          before anything is created. You press the button. Tappy, our AI helper, answers how-to questions for players
          and admins, and hands admins to the setup assistant when they want something built.
        </p>
        <div class="fsa-hero-cta">
          <marketing-cta-btn color="primary" size="large">{{ accountCtaLabel }}</marketing-cta-btn>
          <v-btn variant="outlined" color="primary" size="large" to="/features">See every feature</v-btn>
        </div>
        <p class="fsa-note">Included with Tap Leagues, free.* <commission-note/></p>
      </v-container>
    </section>

    <v-container class="fsa-wrap">
      <!-- ===== THE DEMO ===== -->
      <section class="fsa-demo" aria-labelledby="fsa-demo-title">
        <h2 id="fsa-demo-title" class="fsa-h2">This is the setup assistant, running</h2>
        <p class="fsa-sub">
          A made-up club, the real screens otherwise. Watch the sentence go in, answer the one question it asks, open
          the draft when it pulses, and follow Review and apply through to All set.
        </p>
        <setup-assistant-demo-stage class="fsa-stage"/>
      </section>

      <!-- ===== WHAT IT DRAFTS ===== -->
      <section class="fsa-section" aria-labelledby="fsa-drafts-title">
        <h2 id="fsa-drafts-title" class="fsa-h2">What it drafts</h2>
        <p class="fsa-sub">Each thing it would make is its own card in the draft. Every card opens in its ordinary form, already filled in, so you can fix a price or a date there.</p>
        <div class="fsa-drafts tl-card" role="list">
          <div v-for="d in DRAFTS" :key="d.name" class="fsa-draft" role="listitem">
            <span class="fsa-draft-icon"><v-icon size="18" :icon="d.icon"/></span>
            <h3 class="fsa-draft-name">{{ d.name }}</h3>
            <p class="fsa-draft-blurb">{{ d.blurb }}</p>
          </div>
        </div>
      </section>

      <!-- ===== HOW IT WORKS ===== -->
      <section class="fsa-section" aria-labelledby="fsa-how-title">
        <h2 id="fsa-how-title" class="fsa-h2">How a setup happens</h2>
        <ol class="fsa-steps">
          <li class="tl-card">
            <span class="fsa-step-n">1</span>
            <h3>You describe it</h3>
            <p>In the admin menu, under Setup assistant, or from New competition, Memberships &amp; passes or Bookings. Type what you want, or paste a price list. It asks short questions and remembers lasting facts about your club.</p>
          </li>
          <li class="tl-card">
            <span class="fsa-step-n">2</span>
            <h3>You check the draft</h3>
            <p>Review and apply opens the check page: every item with what is live now and what it will be after, the checks the forms would run, what it assumed and what it cannot do yet. Edit in form, or leave an item out.</p>
          </li>
          <li class="tl-card">
            <span class="fsa-step-n">3</span>
            <h3>You apply it, and can undo it</h3>
            <p>Create setup or Apply now changes the live setup in one go; if any part fails, nothing changes. All set gives you each new competition's invite link. Undo for 7 days, while nobody has joined, paid or booked.</p>
          </li>
        </ol>
      </section>

      <!-- ===== TAPPY ===== -->
      <section class="fsa-section fsa-tappy" aria-labelledby="fsa-tappy-title">
        <div class="fsa-tappy-copy">
          <h2 id="fsa-tappy-title" class="fsa-h2">Tappy, our AI helper</h2>
          <p class="fsa-sub">
            A help chat for anyone signed in, in the language you write in. Ask how to do anything in Tap Leagues and
            Tappy explains the steps, with links to the pages it is talking about. It answers questions; it does not
            change your club. When a club admin asks about setting the club up, it offers to open the setup assistant.
          </p>
          <ul class="fsa-details">
            <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>For players and club admins, on the website and in the app.</li>
            <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>Only answers about using Tap Leagues. If it does not know, it says so.</li>
            <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>Cannot see your account, results or messages, and cannot act for you.</li>
            <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>Rate any reply. Your conversation is kept until you start a new chat.</li>
          </ul>
        </div>
        <tappy-demo-stage class="fsa-tappy-demo"/>
      </section>

      <!-- ===== DETAILS ===== -->
      <section class="fsa-section" aria-labelledby="fsa-details-title">
        <h2 id="fsa-details-title" class="fsa-h2">What else it does</h2>
        <ul class="fsa-details fsa-details--two">
          <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>Starts from what your club already has. Changing a live price shows exactly what changes before it does.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>Things I assumed lists every guess, each with Change. Not possible yet lists what it cannot do, passed to Tap Leagues.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>A new competition is open to joining by its invite link as soon as you apply, with the players you named put in, unless it waits for a membership drafted with it that cannot go live yet.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>Memberships are created unpublished until you publish them (a free one a new competition needs goes live when you apply; a paid one can be published once your payment account is ready). Booking rules stay off for players until you switch bookings on.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>If it cannot answer, your draft is saved and every item opens in its form, already filled in.</li>
          <li><v-icon size="16" icon="mdi-check-circle" class="fsa-check"/>History shows what was applied, by whom, with Undo while the 7 days last.</li>
        </ul>
      </section>

      <!-- ===== FAQ ===== -->
      <section class="fsa-section" aria-labelledby="fsa-faq-title">
        <h2 id="fsa-faq-title" class="fsa-h2">Questions</h2>
        <div class="fsa-faqs">
          <div v-for="faq in FAQS" :key="faq.q" class="fsa-faq tl-card">
            <h3>{{ faq.q }}</h3>
            <p>{{ faq.a }}</p>
          </div>
        </div>
      </section>

      <!-- ===== CTA ===== -->
      <section class="fsa-final">
        <div class="fsa-final-streaks" aria-hidden="true"/>
        <div class="fsa-final-inner">
          <h2 class="fsa-final-title">Describe your club tonight</h2>
          <p class="fsa-final-sub">Set up the club, open the setup assistant, say what you want.</p>
          <div class="fsa-final-cta">
            <marketing-cta-btn color="white" size="large" class="text-primary">{{ accountCtaLabel }}</marketing-cta-btn>
            <v-btn variant="outlined" color="white" size="large" to="/features">All features</v-btn>
          </div>
        </div>
      </section>
    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
.fsa-wrap {
  max-width: 1100px;
}

.fsa-hero {
  position: relative;
  overflow: hidden;
  background:
      radial-gradient(circle at 14% 0%, rgba(211, 41, 70, 0.08), transparent 46%),
      linear-gradient(180deg, rgb(var(--v-theme-surface)) 0%, rgb(var(--v-theme-light)) 100%);
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.fsa-hero-inner {
  padding-top: 32px;
  padding-bottom: 36px;

  @media (min-width: 960px) {
    padding-top: 44px;
    padding-bottom: 48px;
  }
}

.fsa-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
  text-decoration: none;
  margin-bottom: 18px;
}

.fsa-eyebrow {
  display: block;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 12px;
}

.fsa-title {
  font-size: clamp(30px, 5vw, 46px);
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.05;
  max-width: 760px;
}

.fsa-lead {
  font-size: 16px;
  line-height: 1.7;
  color: rgba(var(--v-theme-charcoal), 0.72);
  max-width: 720px;
  margin-top: 14px;
}

.fsa-hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}

.fsa-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 10px;
}

.fsa-h2 {
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
}

.fsa-sub {
  font-size: 15px;
  line-height: 1.65;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 8px;
  max-width: 640px;
}

.fsa-demo {
  margin-top: 40px;

  @media (min-width: 960px) {
    margin-top: 56px;
  }
}

.fsa-stage {
  margin-top: 20px;
}

.fsa-section {
  margin-top: 48px;

  @media (min-width: 960px) {
    margin-top: 64px;
  }
}

/* What it drafts: a compact list */
.fsa-drafts {
  margin-top: 18px;
  overflow: hidden;
}

.fsa-draft {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 2px 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  @media (min-width: 760px) {
    grid-template-columns: 32px minmax(200px, 30%) minmax(0, 1fr);
    gap: 16px;
    align-items: center;
    padding: 11px 16px;
  }
}

.fsa-draft-icon {
  grid-row: 1 / span 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));

  @media (min-width: 760px) {
    grid-row: auto;
  }
}

.fsa-draft-name {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  margin: 0;
}

.fsa-draft-blurb {
  font-size: 13px;
  line-height: 1.5;
  color: rgb(var(--v-theme-charcoal));
  margin: 0;
}

/* Steps */
.fsa-steps {
  list-style: none;
  padding: 0;
  margin: 18px 0 0;
  display: grid;
  gap: 14px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(3, 1fr);
  }

  li {
    padding: 18px;
  }

  h3 {
    font-size: 15px;
    font-weight: 700;
    margin-top: 10px;
  }

  p {
    font-size: 13px;
    line-height: 1.6;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.fsa-step-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 800;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
}

/* Tappy: copy beside the chat */
.fsa-tappy {
  display: grid;
  gap: 20px;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 440px;
    align-items: start;
    gap: 40px;
  }
}

.fsa-details {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: grid;
  gap: 10px;

  &--two {
    @media (min-width: 760px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px 24px;
    }
  }

  li {
    display: flex;
    gap: 9px;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.55;
  }
}

.fsa-check {
  color: rgb(var(--v-theme-success));
  margin-top: 2px;
  flex: none;
}

.fsa-faqs {
  display: grid;
  gap: 14px;
  margin-top: 18px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.fsa-faq {
  padding: 18px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 13.5px;
    line-height: 1.65;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.fsa-final {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin-top: 64px;
  margin-bottom: 24px;
  padding: 56px 24px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #E0314F 0%, #D32946 44%, #A31C35 100%);
  box-shadow: 0 30px 60px -30px rgba(163, 28, 53, 0.75);

  @media (min-width: 960px) {
    margin-top: 88px;
    padding: 72px 40px;
  }
}

.fsa-final-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.05) 38px 40px);
}

.fsa-final-inner {
  position: relative;
  z-index: 1;
}

.fsa-final-title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.8px;

  @media (min-width: 960px) {
    font-size: 36px;
  }
}

.fsa-final-sub {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 10px;
}

.fsa-final-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 26px;
}
</style>
